import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// --- Constant for Rupee symbol ----------------------------------
const RUPEE = '\u20B9'; // Unicode for ?

/**
 * Merge class names with Tailwind CSS classes
 * @param {...any} inputs - Class names to merge
 * @returns {string} Merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency with ? symbol
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return `${RUPEE}0`;  // fixed fallback
  if (amount >= 1e7) return `${RUPEE}${(amount / 1e7).toFixed(1)} Cr`;
  if (amount >= 1e5) return `${RUPEE}${(amount / 1e5).toFixed(1)} L`;
  return `${RUPEE}${Number(amount).toLocaleString('en-IN')}`;
};

/**
 * Format currency in compact form (K, L, Cr)
 * @param {number} amount - Amount to format
 * @returns {string} Compact formatted currency string
 */
export const formatCompactCurrency = (amount) => {
  if (!amount && amount !== 0) return `${RUPEE}0`;  // fixed fallback
  if (amount >= 1e7) return `${RUPEE}${(amount / 1e7).toFixed(1)}Cr`;
  if (amount >= 1e5) return `${RUPEE}${(amount / 1e5).toFixed(1)}L`;
  if (amount >= 1e3) return `${RUPEE}${(amount / 1e3).toFixed(1)}K`;
  return `${RUPEE}${amount}`;
};

/**
 * Format date to DD-MM-YYYY
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return '�';
  try {
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid date';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format date to DD MMM YYYY
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '�';
  try {
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid date';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format time to HH:MM AM/PM
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted time
 */
export const formatTime = (dateString) => {
  if (!dateString) return '�';
  try {
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid time';
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return 'Invalid time';
  }
};

/**
 * Format date and time
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '�';
  try {
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid date';
    return `${formatDate(dateString)} ${formatTime(dateString)}`;
  } catch {
    return 'Invalid date';
  }
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} length - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, length = 50) => {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
};

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Generate a random ID
 * @param {number} length - Length of ID (default: 8)
 * @returns {string} Random ID
 */
export const generateId = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Check if string is valid email
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Check if string is valid phone number (Indian format)
 * @param {string} phone - Phone to validate
 * @returns {boolean} Is valid phone
 */
export const isValidPhone = (phone) => {
  const regex = /^[6-9]\d{9}$/;
  return regex.test(phone);
};

/**
 * Get status color for badges
 * @param {string} status - Status string
 * @returns {string} Color class
 */
export const getStatusColor = (status) => {
  const statusMap = {
    'active': 'bg-green-100 text-green-700 border-green-200',
    'inactive': 'bg-red-100 text-red-700 border-red-200',
    'pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'approved': 'bg-green-100 text-green-700 border-green-200',
    'rejected': 'bg-red-100 text-red-700 border-red-200',
    'verified': 'bg-green-100 text-green-700 border-green-200',
    'ongoing': 'bg-green-100 text-green-700 border-green-200',
    'upcoming': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'completed': 'bg-blue-100 text-blue-700 border-blue-200',
    'on-hold': 'bg-red-100 text-red-700 border-red-200',
    'construction': 'bg-orange-100 text-orange-700 border-orange-200',
    'interior': 'bg-purple-100 text-purple-700 border-purple-200',
    'partner-construction': 'bg-orange-100 text-orange-700 border-orange-200',
    'partner-interior': 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return statusMap[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

/**
 * Get status label
 * @param {string} status - Status string
 * @returns {string} Formatted status label
 */
export const getStatusLabel = (status) => {
  const labelMap = {
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'verified': 'Verified',
    'ongoing': 'Ongoing',
    'upcoming': 'Upcoming',
    'completed': 'Completed',
    'on-hold': 'On Hold',
    'construction': 'Construction',
    'interior': 'Interior',
    'partner-construction': 'Construction',
    'partner-interior': 'Interior',
  };
  return labelMap[status] || status?.replace(/_/g, ' ') || 'Unknown';
};

/**
 * Group array by key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object} Grouped object
 */
export const groupBy = (array, key) => {
  if (!array || !Array.isArray(array)) return {};
  return array.reduce((result, item) => {
    const groupKey = item[key] || 'undefined';
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

/**
 * Deep clone object
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
  if (!obj) return obj;
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {*} value - Value to check
 * @returns {boolean} Is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

// --- Default export (optional) ----------------------------------
export default {
  cn,
  formatCurrency,
  formatCompactCurrency,
  formatDate,
  formatDateLong,
  formatTime,
  formatDateTime,
  truncateText,
  capitalizeWords,
  generateId,
  getInitials,
  isValidEmail,
  isValidPhone,
  getStatusColor,
  getStatusLabel,
  groupBy,
  debounce,
  deepClone,
  isEmpty,
};